import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function PharmacyRequests({ currentUser }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'active' | 'all'

  useEffect(() => {
    fetchPharmacies();
  }, [filter]);

  const fetchPharmacies = async () => {
    setLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };

      // Pour les pending, on peut utiliser /pharmacies/pending qui enrichit avec owner
      if (filter === 'pending') {
        const response = await axios.get(`${API}/pharmacies/pending`, { headers });
        setPharmacies(response.data);
      } else {
        // Pour active / all : on charge toutes puis on filtre + enrichit côté client
        const response = await axios.get(`${API}/pharmacies`, { headers });
        let list = response.data || [];

        if (filter === 'active') {
          list = list.filter(p => p.status === true);
        }

        // Enrichir avec owner (depuis /users)
        try {
          const usersResp = await axios.get(`${API}/users`, { headers });
          const usersById = {};
          (usersResp.data || []).forEach(u => { usersById[u.id] = u; });
          list = list.map(p => ({
            ...p,
            owner: p.owner_id ? usersById[p.owner_id] : null
          }));
        } catch (e) {
          // Ignore enrichment failure - keep base list
        }

        setPharmacies(list);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (pharmacyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir approuver cette pharmacie ?')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.put(`${API}/pharmacies/${pharmacyId}/approve`, {}, { headers });
      toast.success('Pharmacie approuvée avec succès !');
      fetchPharmacies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (pharmacyId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser cette demande ? La pharmacie sera supprimée définitivement.')) return;

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      await axios.put(`${API}/pharmacies/${pharmacyId}/reject`, {}, { headers });
      toast.success('Demande refusée');
      fetchPharmacies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  const getStatusBadge = (status) => {
    if (status === true) {
      return (
        <Badge className="flex items-center gap-1 w-fit bg-green-100 text-green-700 hover:bg-green-100">
          <CheckCircle className="w-3 h-3" />
          Active
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="flex items-center gap-1 w-fit">
        <Clock className="w-3 h-3" />
        En attente
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="pharmacy-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Demandes d'Inscription</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Validez ou refusez les nouvelles pharmacies</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
          data-testid="filter-pending-btn"
        >
          En attente
        </Button>
        <Button
          variant={filter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('active')}
          data-testid="filter-active-btn"
        >
          Approuvées
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          data-testid="filter-all-btn"
        >
          Toutes
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Liste des demandes ({pharmacies.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Chargement...</p>
          ) : pharmacies.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Aucune demande trouvée</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pharmacie</TableHead>
                  <TableHead>Propriétaire</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pharmacies.map((pharmacy) => {
                  const owner = pharmacy.owner || {};
                  const isPending = pharmacy.status === false;
                  return (
                    <TableRow key={pharmacy.id} data-testid="pharmacy-row">
                      <TableCell>
                        <div>
                          <div className="font-medium">{pharmacy.name}</div>
                          <div className="text-sm text-gray-500">{pharmacy.address || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell>{owner.full_name || '-'}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {owner.username || '-'}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {owner.phone && <div>📞 {owner.phone}</div>}
                          {owner.email && <div>✉️ {owner.email}</div>}
                          {!owner.phone && !owner.email && <span className="text-gray-400">-</span>}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {pharmacy.created_at
                          ? format(new Date(pharmacy.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })
                          : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(pharmacy.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {isPending && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleApprove(pharmacy.id)}
                                data-testid="approve-btn"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleReject(pharmacy.id)}
                                data-testid="reject-btn"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Refuser
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PharmacyRequests;
